import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  FileText, Upload, Trash2, Download, Loader2, Search,
  FolderOpen, Lock, AlertCircle, Check, Eye, Plus, X, File,
  Image, FileSpreadsheet, FileCode, FileArchive
} from 'lucide-react'

interface SharedFile {
  id: string
  name: string
  size: number
  created_at: string
  url: string
  bucket_path: string
  section: 'public' | 'private'
}

const FILE_ICONS: Record<string, { icon: typeof FileText; color: string }> = {
  pdf: { icon: FileText, color: 'text-red-500 bg-red-50' },
  doc: { icon: FileText, color: 'text-blue-500 bg-blue-50' },
  docx: { icon: FileText, color: 'text-blue-500 bg-blue-50' },
  xls: { icon: FileSpreadsheet, color: 'text-emerald-500 bg-emerald-50' },
  xlsx: { icon: FileSpreadsheet, color: 'text-emerald-500 bg-emerald-50' },
  csv: { icon: FileSpreadsheet, color: 'text-green-500 bg-green-50' },
  jpg: { icon: Image, color: 'text-amber-500 bg-amber-50' },
  jpeg: { icon: Image, color: 'text-amber-500 bg-amber-50' },
  png: { icon: Image, color: 'text-purple-500 bg-purple-50' },
  gif: { icon: Image, color: 'text-pink-500 bg-pink-50' },
  webp: { icon: Image, color: 'text-indigo-500 bg-indigo-50' },
  zip: { icon: FileArchive, color: 'text-slate-500 bg-slate-50' },
  rar: { icon: FileArchive, color: 'text-slate-500 bg-slate-50' },
  json: { icon: FileCode, color: 'text-cyan-500 bg-cyan-50' },
  html: { icon: FileCode, color: 'text-orange-500 bg-orange-50' },
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  return FILE_ICONS[ext] || { icon: File, color: 'text-slate-400 bg-slate-50' }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const BUCKET = 'shared-files'

export function SharedFiles() {
  const { isSuperAdmin } = useAuth()
  const [activeSection, setActiveSection] = useState<'public' | 'private'>('public')
  const [files, setFiles] = useState<SharedFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [dragOver, setDragOver] = useState(false)

  const fetchFiles = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const sections: ('public' | 'private')[] = isSuperAdmin ? ['public', 'private'] : ['public']
      const allFiles: SharedFile[] = []

      for (const section of sections) {
        const { data, error: listErr } = await supabase.storage
          .from(BUCKET)
          .list(section, { limit: 200, sortBy: { column: 'created_at', order: 'desc' } })

        if (listErr) {
          console.warn(`Error listing ${section}:`, listErr.message)
          continue
        }

        if (data) {
          for (const file of data) {
            if (file.name === '.emptyFolderPlaceholder') continue
            const path = `${section}/${file.name}`
            const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path)
            allFiles.push({
              id: file.id || path,
              name: file.name,
              size: file.metadata?.size || 0,
              created_at: file.created_at || new Date().toISOString(),
              url: urlData.publicUrl,
              bucket_path: path,
              section,
            })
          }
        }
      }
      setFiles(allFiles)
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento')
    }
    setLoading(false)
  }, [isSuperAdmin])

  useEffect(() => { fetchFiles() }, [fetchFiles])

  // Clear messages
  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(''); setError('') }, 4000)
      return () => clearTimeout(t)
    }
  }, [success, error])

  async function handleUpload(fileList: FileList | null) {
    if (!fileList || fileList.length === 0 || !isSuperAdmin) return
    setUploading(true)
    setError('')
    let count = 0

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const timestamp = Date.now()
      const path = `${activeSection}/${timestamp}_${safeName}`

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false })

      if (uploadErr) {
        setError(`Errore upload "${file.name}": ${uploadErr.message}`)
      } else {
        count++
      }
    }

    if (count > 0) {
      setSuccess(`✅ ${count} file caricati con successo`)
      await fetchFiles()
    }
    setUploading(false)
  }

  async function handleDelete(file: SharedFile) {
    if (!isSuperAdmin) return
    if (!confirm(`Eliminare definitivamente "${file.name}"?`)) return

    const { error: delErr } = await supabase.storage
      .from(BUCKET)
      .remove([file.bucket_path])

    if (delErr) {
      setError(delErr.message)
    } else {
      setSuccess(`🗑️ "${file.name}" eliminato`)
      setFiles(prev => prev.filter(f => f.id !== file.id))
    }
  }

  async function handleDownload(file: SharedFile) {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from(BUCKET)
        .download(file.bucket_path)

      if (dlErr) throw dlErr

      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      a.download = file.name.replace(/^\d+_/, '') // Strip timestamp prefix
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(`Errore download: ${e.message}`)
    }
  }

  // Drag & Drop
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    if (isSuperAdmin) setDragOver(true)
  }
  function handleDragLeave() { setDragOver(false) }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (isSuperAdmin) handleUpload(e.dataTransfer.files)
  }

  // Filtered files for active section
  const sectionFiles = files.filter(f => f.section === activeSection)
  const filteredFiles = sectionFiles.filter(f =>
    f.name.replace(/^\d+_/, '').toLowerCase().includes(search.toLowerCase())
  )

  // If not super admin, don't show private tab
  const canSeePrivate = isSuperAdmin

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-slate-800">Archivio Progetto</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {isSuperAdmin
              ? 'Gestisci file pubblici e il tuo archivio personale'
              : 'Consulta e scarica i documenti di progetto'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <FolderOpen className="w-4 h-4" />
          {files.length} file totali
        </div>
      </div>

      {/* Messages */}
      {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700"><AlertCircle className="w-4 h-4" />{error}</div>}
      {success && <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700"><Check className="w-4 h-4" />{success}</div>}

      {/* Section Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        <button
          onClick={() => setActiveSection('public')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
            activeSection === 'public' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          <Eye className="w-3.5 h-3.5" /> 📂 File Consultabili
        </button>
        {canSeePrivate && (
          <button
            onClick={() => setActiveSection('private')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
              activeSection === 'private' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <Lock className="w-3.5 h-3.5" /> 🔒 File Personali
          </button>
        )}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca file..."
          className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Upload Area (super admin only) */}
      {isSuperAdmin && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
            dragOver
              ? 'border-indigo-400 bg-indigo-50'
              : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50'
          }`}
        >
          <input
            type="file"
            multiple
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
            onChange={e => { handleUpload(e.target.files); e.target.value = '' }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-3 text-indigo-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-bold">Caricamento in corso...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeSection === 'private' ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'
              }`}>
                <Upload className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700">
                  Trascina qui i file o clicca per caricare
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {activeSection === 'public'
                    ? 'I file caricati qui saranno visibili a tutti gli utenti'
                    : 'I file caricati qui saranno visibili solo a te'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento file...
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="text-center py-16">
          <div className={`w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4 ${
            activeSection === 'private' ? 'bg-amber-50 text-amber-400' : 'bg-slate-50 text-slate-300'
          }`}>
            {activeSection === 'private' ? <Lock className="w-8 h-8" /> : <FolderOpen className="w-8 h-8" />}
          </div>
          <p className="text-sm font-bold text-slate-500">
            {search ? 'Nessun file trovato' : activeSection === 'private' ? 'Il tuo archivio personale è vuoto' : 'Nessun file consultabile'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {isSuperAdmin
              ? 'Trascina o carica un file per iniziare'
              : 'Non sono ancora stati caricati file in questa sezione'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map(file => {
            const { icon: Icon, color } = getFileIcon(file.name)
            const displayName = file.name.replace(/^\d+_/, '') // Strip timestamp prefix
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-200 hover:shadow-md hover:border-indigo-200 transition-all group"
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-800 truncate" title={displayName}>
                    {displayName}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                    <span>{formatFileSize(file.size)}</span>
                    <span>•</span>
                    <span>{new Date(file.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    {file.section === 'private' && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-0.5 text-amber-500 font-bold"><Lock className="w-3 h-3" /> Personale</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 hover:bg-emerald-50 rounded-xl text-emerald-600 transition-colors"
                    title="Apri / Visualizza"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-2 hover:bg-indigo-50 rounded-xl text-indigo-600 transition-colors"
                    title="Scarica"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  {isSuperAdmin && (
                    <button
                      onClick={() => handleDelete(file)}
                      className="p-2 hover:bg-red-50 rounded-xl text-red-500 transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Footer info */}
      <div className="text-center text-[10px] text-slate-300 uppercase tracking-wider pt-4">
        {activeSection === 'public' ? '📂 File visibili a tutti gli utenti' : '🔒 File visibili solo a te'}
        {filteredFiles.length > 0 && ` • ${filteredFiles.length} file`}
      </div>
    </div>
  )
}
